import { useScreenDimensions } from "@mike/crt";
import { useFrame } from "@react-three/fiber";
import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";

// Sky colors for different times of day - bright and colorful
const SKY_PALETTES = {
  night: {
    top: new THREE.Color("#0f1848"),
    bottom: new THREE.Color("#1a4070"),
    mountain1: new THREE.Color("#1a3858"),
    mountain2: new THREE.Color("#153050"),
    mountain3: new THREE.Color("#102848"),
    hill: new THREE.Color("#104838"),
    ground: new THREE.Color("#0a3828"),
    tree: new THREE.Color("#052818"),
    starOpacity: 1.0,
  },
  dawn: {
    top: new THREE.Color("#3080c0"),
    bottom: new THREE.Color("#f0c860"),
    mountain1: new THREE.Color("#584858"),
    mountain2: new THREE.Color("#483848"),
    mountain3: new THREE.Color("#382838"),
    hill: new THREE.Color("#2a5040"),
    ground: new THREE.Color("#1f4030"),
    tree: new THREE.Color("#103020"),
    starOpacity: 0.2,
  },
  day: {
    top: new THREE.Color("#2090e0"),
    bottom: new THREE.Color("#60d0ff"),
    mountain1: new THREE.Color("#6090b0"),
    mountain2: new THREE.Color("#5080a0"),
    mountain3: new THREE.Color("#407090"),
    hill: new THREE.Color("#308858"),
    ground: new THREE.Color("#206848"),
    tree: new THREE.Color("#104838"),
    starOpacity: 0,
  },
  dusk: {
    top: new THREE.Color("#182050"),
    bottom: new THREE.Color("#e07048"),
    mountain1: new THREE.Color("#4a3070"),
    mountain2: new THREE.Color("#382560"),
    mountain3: new THREE.Color("#281a50"),
    hill: new THREE.Color("#253838"),
    ground: new THREE.Color("#1a2828"),
    tree: new THREE.Color("#0a1818"),
    starOpacity: 0.5,
  },
};

type TimeOfDay = keyof typeof SKY_PALETTES;

function getTimeOfDay(): TimeOfDay {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 8) return "dawn";
  if (hour >= 8 && hour < 17) return "day";
  if (hour >= 17 && hour < 20) return "dusk";
  return "night";
}

// Create mountain geometry with procedural peaks
function createMountainGeometry(
  width: number,
  height: number,
  segments: number,
  seed: number,
  amplitude: number,
): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  const vertices: number[] = [];
  const indices: number[] = [];

  // Generate top edge with sine waves - always positive with base offset
  const topPoints: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const x = (i / segments) * width - width / 2;
    // Multiple sine waves at different frequencies for natural variation
    const wave1 = Math.sin(x * 0.006 + seed) * amplitude * 0.4;
    const wave2 = Math.sin(x * 0.012 + seed * 2.3) * amplitude * 0.3;
    const wave3 = Math.sin(x * 0.025 + seed * 3.7) * amplitude * 0.2;
    const wave4 = Math.sin(x * 0.045 + seed * 5.1) * amplitude * 0.1;
    // Add base offset so mountains always have peaks (never flat)
    const baseHeight = amplitude * 0.3;
    topPoints.push(baseHeight + wave1 + wave2 + wave3 + wave4);
  }

  // Create vertices
  for (let i = 0; i <= segments; i++) {
    const x = (i / segments) * width - width / 2;
    vertices.push(x, topPoints[i] ?? 0, 0);
    vertices.push(x, -height, 0);
  }

  // Create faces
  for (let i = 0; i < segments; i++) {
    const topLeft = i * 2;
    const bottomLeft = i * 2 + 1;
    const topRight = (i + 1) * 2;
    const bottomRight = (i + 1) * 2 + 1;
    indices.push(topLeft, bottomLeft, topRight);
    indices.push(bottomLeft, bottomRight, topRight);
  }

  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(vertices, 3),
  );
  geometry.setIndex(indices);
  return geometry;
}

// Create rolling hills geometry with gentler waves
function createHillGeometry(
  width: number,
  height: number,
  segments: number,
  seed: number,
  amplitude: number,
): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  const vertices: number[] = [];
  const indices: number[] = [];

  // Generate top edge with gentler rolling waves
  const topPoints: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const x = (i / segments) * width - width / 2;
    const noise =
      Math.sin(x * 0.004 + seed) * amplitude * 0.6 +
      Math.sin(x * 0.01 + seed * 2) * amplitude * 0.3 +
      Math.sin(x * 0.02 + seed * 3) * amplitude * 0.1;
    topPoints.push(noise);
  }

  // Create vertices
  for (let i = 0; i <= segments; i++) {
    const x = (i / segments) * width - width / 2;
    vertices.push(x, topPoints[i] ?? 0, 0);
    vertices.push(x, -height, 0);
  }

  // Create faces
  for (let i = 0; i < segments; i++) {
    const topLeft = i * 2;
    const bottomLeft = i * 2 + 1;
    const topRight = (i + 1) * 2;
    const bottomRight = (i + 1) * 2 + 1;
    indices.push(topLeft, bottomLeft, topRight);
    indices.push(bottomLeft, bottomRight, topRight);
  }

  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(vertices, 3),
  );
  geometry.setIndex(indices);
  return geometry;
}

// Create a simple tree geometry (triangle + rectangle trunk)
function createTreeGeometry(
  trunkHeight: number,
  trunkWidth: number,
  foliageHeight: number,
  foliageWidth: number,
): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  const vertices: number[] = [];
  const indices: number[] = [];

  // Trunk (rectangle)
  const tw = trunkWidth / 2;
  const th = trunkHeight;
  vertices.push(-tw, 0, 0);
  vertices.push(tw, 0, 0);
  vertices.push(tw, th, 0);
  vertices.push(-tw, th, 0);
  indices.push(0, 1, 2, 0, 2, 3);

  // Foliage layers (3 triangles stacked)
  const layers = 3;
  for (let i = 0; i < layers; i++) {
    const layerWidth = foliageWidth * (1 - i * 0.2);
    const layerY = th + (foliageHeight / layers) * i;
    const layerH = foliageHeight / layers + foliageHeight * 0.15;
    const baseIdx = 4 + i * 3;

    vertices.push(-layerWidth / 2, layerY, 0);
    vertices.push(layerWidth / 2, layerY, 0);
    vertices.push(0, layerY + layerH, 0);

    indices.push(baseIdx, baseIdx + 1, baseIdx + 2);
  }

  geometry.setAttribute(
    "position",
    new THREE.Float32BufferAttribute(vertices, 3),
  );
  geometry.setIndex(indices);
  return geometry;
}

/**
 * Procedural landscape background with parallax scrolling
 * Features: sky gradient, stars, mountains, hills, and trees
 */
export function SceneBackground() {
  const { width, height } = useScreenDimensions();
  const groupRef = useRef<THREE.Group>(null);
  const scrollRef = useRef(0);
  const [timeOfDay] = useState<TimeOfDay>(getTimeOfDay);

  const palette = SKY_PALETTES[timeOfDay];

  // Create sky gradient material
  const skyMaterial = useMemo(() => {
    return new THREE.ShaderMaterial({
      uniforms: {
        topColor: { value: palette.top },
        bottomColor: { value: palette.bottom },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        varying vec2 vUv;
        void main() {
          gl_FragColor = vec4(mix(bottomColor, topColor, vUv.y), 1.0);
        }
      `,
      side: THREE.DoubleSide,
    });
  }, [palette]);

  // Generate stars
  const stars = useMemo(() => {
    const starData: Array<{
      x: number;
      y: number;
      size: number;
      twinkleSpeed: number;
    }> = [];
    for (let i = 0; i < 100; i++) {
      starData.push({
        x: (Math.random() - 0.5) * width,
        y: Math.random() * height * 0.5 + height * 0.1,
        size: Math.random() * 2 + 1,
        twinkleSpeed: Math.random() * 2 + 1,
      });
    }
    return starData;
  }, [width, height]);

  // Create mountain geometries
  const mountains = useMemo(() => {
    return [
      {
        geometry: createMountainGeometry(
          width * 2.5,
          height * 1.5,
          200,
          1.0,
          height * 0.18,
        ),
        color: palette.mountain1,
        y: -height * 0.02,
        z: -4,
        speed: 0.015,
      },
      {
        geometry: createMountainGeometry(
          width * 2.5,
          height * 1.5,
          300,
          2.5,
          height * 0.14,
        ),
        color: palette.mountain2,
        y: -height * 0.1,
        z: -3,
        speed: 0.03,
      },
      {
        geometry: createMountainGeometry(
          width * 2.5,
          height * 1.5,
          120,
          4.0,
          height * 0.11,
        ),
        color: palette.mountain3,
        y: -height * 0.16,
        z: -2,
        speed: 0.05,
      },
    ];
  }, [width, height, palette]);

  // Create hill geometries (wavy rolling hills)
  const hills = useMemo(() => {
    return [
      {
        geometry: createHillGeometry(
          width * 2.5,
          height,
          80,
          5.0,
          height * 0.06,
        ),
        color: palette.hill,
        y: -height * 0.22,
        z: -1,
        speed: 0.07,
      },
      {
        geometry: createHillGeometry(
          width * 2.5,
          height,
          60,
          7.0,
          height * 0.04,
        ),
        color: palette.ground,
        y: -height * 0.3,
        z: 0,
        speed: 0.1,
      },
    ];
  }, [width, height, palette]);

  // Create tree geometry (reused for all trees)
  const treeGeometry = useMemo(() => {
    return createTreeGeometry(
      height * 0.02,
      height * 0.008,
      height * 0.06,
      height * 0.04,
    );
  }, [height]);

  // Generate tree positions
  const trees = useMemo(() => {
    const treeData: Array<{ x: number; scale: number; seed: number }> = [];
    const spacing = 60;
    for (let x = -width; x < width * 2; x += spacing) {
      const variation = (Math.random() - 0.5) * spacing * 0.5;
      treeData.push({
        x: x + variation,
        scale: 0.7 + Math.random() * 0.6,
        seed: Math.random() * 1000,
      });
    }
    return treeData;
  }, [width]);

  // Star twinkle ref
  const starTwinkleRef = useRef(0);

  // Animate - slow parallax scrolling
  useFrame((_, delta) => {
    scrollRef.current += delta * -120; // Base scroll speed
    starTwinkleRef.current += delta;

    if (groupRef.current) {
      // Animate mountains - slowest (far away)
      const mountainMeshes = groupRef.current.children.filter(
        (c) => c.userData.type === "mountain",
      );
      mountainMeshes.forEach((mesh, i) => {
        const mountain = mountains[i];
        if (mountain) {
          mesh.position.x = -(
            (scrollRef.current * mountain.speed) %
            (width * 0.8)
          );
        }
      });

      // Animate hills - medium speed
      const hillMeshes = groupRef.current.children.filter(
        (c) => c.userData.type === "hill",
      );
      hillMeshes.forEach((mesh, i) => {
        const hill = hills[i];
        if (hill) {
          mesh.position.x = -((scrollRef.current * hill.speed) % (width * 0.8));
        }
      });

      // Animate trees - fastest (foreground)
      const treeMeshes = groupRef.current.children.filter(
        (c) => c.userData.type === "tree",
      );
      treeMeshes.forEach((mesh, i) => {
        const tree = trees[i];
        if (tree) {
          const baseX = tree.x - scrollRef.current * 0.15;
          mesh.position.x =
            (((baseX % (width * 2)) + width * 2) % (width * 2)) - width;
        }
      });

      // Animate stars (gentle twinkle)
      const starMeshes = groupRef.current.children.filter(
        (c) => c.userData.type === "star",
      );
      starMeshes.forEach((mesh, i) => {
        const star = stars[i];
        if (star && mesh instanceof THREE.Mesh) {
          const twinkle =
            Math.sin(starTwinkleRef.current * star.twinkleSpeed + i) * 0.5 +
            0.5;
          (mesh.material as THREE.MeshBasicMaterial).opacity =
            palette.starOpacity * (0.3 + twinkle * 0.7);
        }
      });
    }
  });

  // Cleanup
  useEffect(() => {
    return () => {
      skyMaterial.dispose();
      for (const m of mountains) {
        m.geometry.dispose();
      }
      for (const h of hills) {
        h.geometry.dispose();
      }
      treeGeometry.dispose();
    };
  }, [skyMaterial, mountains, hills, treeGeometry]);

  return (
    <group ref={groupRef}>
      {/* Sky gradient */}
      <mesh position={[0, height * 0.1, -6]}>
        <planeGeometry args={[width * 1.5, height * 1.5]} />
        <primitive object={skyMaterial} attach="material" />
      </mesh>

      {/* Stars (only visible at night/dusk) */}
      {palette.starOpacity > 0 &&
        stars.map((star, i) => (
          <mesh
            key={`star-${i}`}
            position={[star.x, star.y, -5.5]}
            userData={{ type: "star" }}
          >
            <circleGeometry args={[star.size, 8]} />
            <meshBasicMaterial
              color="#ffffff"
              transparent
              opacity={palette.starOpacity}
            />
          </mesh>
        ))}

      {/* Mountain layers */}
      {mountains.map((mountain, i) => (
        <mesh
          key={`mountain-${i}`}
          position={[0, mountain.y, mountain.z]}
          userData={{ type: "mountain" }}
        >
          <primitive object={mountain.geometry} attach="geometry" />
          <meshBasicMaterial color={mountain.color} />
        </mesh>
      ))}

      {/* Rolling hills */}
      {hills.map((hill, i) => (
        <mesh
          key={`hill-${i}`}
          position={[0, hill.y, hill.z]}
          userData={{ type: "hill" }}
        >
          <primitive object={hill.geometry} attach="geometry" />
          <meshBasicMaterial color={hill.color} />
        </mesh>
      ))}

      {/* Trees */}
      {trees.map((tree, i) => (
        <mesh
          key={`tree-${i}`}
          position={[tree.x, -height * 0.32, 1]}
          scale={[tree.scale, tree.scale, 1]}
          userData={{ type: "tree" }}
        >
          <primitive object={treeGeometry} attach="geometry" />
          <meshBasicMaterial color={palette.tree} />
        </mesh>
      ))}
    </group>
  );
}
